import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import CancelIcon from '@mui/icons-material/Cancel';
import InventoryIcon from '@mui/icons-material/Inventory2';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import receivingService, { ReceivingItemDto, ReceiveItemResult, ReceiveVariantCandidate, PutawaySuggestion } from '../../services/receivingService';
import ReceivingNavMap from './ReceivingNavMap';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsPrimaryActionBar from '../../shared/WmsPrimaryActionBar';
import WmsListSection from '../../shared/WmsListSection';
import { useAuth } from '../../../../contexts/AuthContext';
import { InventoryService, CreateInventoryItemDto } from '../../../accounting/inventory/services/inventoryService';
import { InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReceivingScanningProps {
  session: {
    id: string;
    sessionNumber: string;
    supplierName?: string;
    documentNumber?: string;
    cashierName?: string;
    startedAt?: string;
  };
  warehouseId: string;
  items: ReceivingItemDto[];
  onItemsChange: (items: ReceivingItemDto[]) => void;
  onPutaway: (item: ReceivingItemDto) => void;
  onBulkPutaway: () => void;
  onComplete: () => Promise<void> | void;
  // Devam eden mal kabulü tamamen iptal eder (stoklar geri alınır, fiş "Cancelled" olur).
  onCancel: (cancelReason?: string) => Promise<void> | void;
  // Hızlı başlatta okutulan barkod: tarama ekranı açılır açılmaz bir kez işlenir
  // (varyant seçimi dahil normal okutma akışından geçer).
  quickStartBarcode?: string | null;
  onQuickStartConsumed?: () => void;
}

interface ScannedProduct {
  id: string;
  productId: string;
  productName: string;
  barcode: string;
  quantity: number;
}

const ReceivingScanning: React.FC<ReceivingScanningProps> = ({
  session, warehouseId, items, onItemsChange, onPutaway, onBulkPutaway, onComplete, onCancel,
  quickStartBarcode, onQuickStartConsumed,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();
  const { userDetails } = useAuth();
  const [lastScanned, setLastScanned] = useState<ScannedProduct | null>(null);
  const [pendingQty, setPendingQty] = useState(1);
  // Son okutulan ürünün önerilen/mevcut lokasyonu — haritada vurgulanır.
  const [scanSuggestion, setScanSuggestion] = useState<PutawaySuggestion | null>(null);
  // Aynı barkodu paylaşan birden fazla varyant olduğunda kullanıcıya seçtirilen liste.
  const [variantChoice, setVariantChoice] = useState<{ barcode: string; variants: ReceiveVariantCandidate[] } | null>(null);
  // Ürün envanterde yoksa: hızlı ekleme dialogu.
  const [notFoundBarcode, setNotFoundBarcode] = useState<string | null>(null);
  const [newProductName, setNewProductName] = useState('');
  const [newProductPrice, setNewProductPrice] = useState<number | ''>('');
  const [newProductVat, setNewProductVat] = useState<number | ''>(20);
  const [creatingProduct, setCreatingProduct] = useState(false);
  // Mal kabul iptal onay dialogu.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // Mal kabul özet/ayrıntı popup'ı (sağ üstteki rozete tıklanınca).
  const [detailOpen, setDetailOpen] = useState(false);

  // Son okutulan ürün için putaway önerisini çek → haritada hedef rafı göster.
  const lastScannedProductId = lastScanned?.productId;
  useEffect(() => {
    if (!warehouseId || !lastScannedProductId) {
      setScanSuggestion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sug = await receivingService.getPutawaySuggestion(warehouseId, lastScannedProductId);
        if (!cancelled) setScanSuggestion(sug);
      } catch {
        if (!cancelled) setScanSuggestion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId, lastScannedProductId]);

  // Başarılı kabul sonucunu listeye işleyen ortak yardımcı.
  const applyReceivedItem = useCallback((result: ReceiveItemResult, fallbackBarcode: string) => {
    const item = result.data!;
    setLastScanned({
      id: item.id,
      productId: item.productId,
      productName: item.productName || t('screens.receiving.scanning.unknownProduct'),
      barcode: item.barcode || fallbackBarcode,
      quantity: item.receivedQuantity,
    });
    setPendingQty(item.receivedQuantity);

    onItemsChange(
      items.some(i => i.id === item.id)
        ? items.map(i => i.id === item.id ? { ...i, receivedQuantity: item.receivedQuantity } : i)
        : [...items, item]
    );
    notify({ type: 'success', message: t('screens.receiving.scanning.receivedToast', { name: item.productName || t('screens.receiving.scanning.productFallback') }) });
  }, [items, onItemsChange, notify, t]);

  const handleScan = useCallback(async (barcode: string) => {
    try {
      const result = await receivingService.receiveItem(session.id, barcode, 1);
      if (result.success && result.data) {
        applyReceivedItem(result, barcode);
      } else if (result.requiresVariantSelection && result.variants?.length) {
        // Barkod birden fazla varyanta ait: kullanıcı hangisini kabul ettiğini seçsin.
        setVariantChoice({ barcode, variants: result.variants });
      } else if (result.notFound) {
        // Ürün envanterde yok → hızlı ekleme dialogunu aç (barkod önceden dolu).
        setNewProductName('');
        setNewProductPrice('');
        setNewProductVat(20);
        setNotFoundBarcode(barcode);
      } else {
        notify({ type: 'error', message: result.message || t('screens.receiving.scanning.errorGeneric') });
      }
    } catch {
      notify({ type: 'error', message: t('screens.receiving.scanning.errorReceive') });
    }
  }, [session.id, applyReceivedItem, notify, t]);

  // Hızlı başlat barkodunu (ready ekranında okutulan) tarama ekranı açılınca bir kez işle.
  const quickStartRef = useRef<string | null>(null);
  useEffect(() => {
    if (quickStartBarcode && quickStartRef.current !== quickStartBarcode) {
      quickStartRef.current = quickStartBarcode;
      handleScan(quickStartBarcode);
      onQuickStartConsumed?.();
    }
  }, [quickStartBarcode, handleScan, onQuickStartConsumed]);

  // Kullanıcı modaldan varyant seçtiğinde, kesin ProductId ile kabulü tekrarla.
  const handleVariantSelect = useCallback(async (variant: ReceiveVariantCandidate) => {
    if (!variantChoice) return;
    try {
      const result = await receivingService.receiveItem(session.id, variantChoice.barcode, 1, variant.productId);
      if (result.success && result.data) {
        applyReceivedItem(result, variantChoice.barcode);
      } else {
        notify({ type: 'error', message: result.message || t('screens.receiving.scanning.variantAcceptFailed') });
      }
    } catch {
      notify({ type: 'error', message: t('screens.receiving.scanning.errorVariantReceive') });
    } finally {
      setVariantChoice(null);
    }
  }, [session.id, variantChoice, applyReceivedItem, notify, t]);

  // Envanterde olmayan ürünü hızlıca oluştur, oturuma kabul et ve raf yerleştirmeye yönlendir.
  const handleCreateAndReceive = useCallback(async () => {
    if (!notFoundBarcode) return;
    const companyId = userDetails?.companyId;
    if (!companyId) {
      notify({ type: 'error', message: t('screens.receiving.scanning.errorNoCompany') });
      return;
    }
    if (!newProductName.trim()) {
      notify({ type: 'warning', message: t('screens.receiving.scanning.errorProductNameRequired') });
      return;
    }

    setCreatingProduct(true);
    try {
      // Stok kodu üret (ad + rastgele) ve barkodu okutulan değere sabitle.
      const stockCode = `${newProductName.trim().substring(0, 3).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
      const dto: CreateInventoryItemDto = {
        companyId,
        name: newProductName.trim(),
        stockCode,
        barcode: notFoundBarcode,
        salePrice: newProductPrice === '' ? 0 : Number(newProductPrice),
        saleCurrency: 'TRY',
        unit: 'adet',
        canBeNegative: true,
        vatRate: newProductVat === '' ? 0 : Number(newProductVat),
        vatStatus: 'excluded',
        description: t('screens.receiving.scanning.quickAddDescription'),
        isActive: true,
      };

      const created: any = await InventoryService.createItem(dto);
      const productId = created?.id || created?.Id;
      if (!productId) {
        notify({ type: 'error', message: t('screens.receiving.scanning.errorProductIdMissing') });
        return;
      }

      // Oluşturulan ürünü kesin ProductId ile oturuma kabul et.
      const result = await receivingService.receiveItem(session.id, notFoundBarcode, pendingQty || 1, productId);
      if (result.success && result.data) {
        const item = result.data;
        onItemsChange(
          items.some(i => i.id === item.id)
            ? items.map(i => i.id === item.id ? { ...i, receivedQuantity: item.receivedQuantity } : i)
            : [...items, item]
        );
        notify({ type: 'success', message: t('screens.receiving.scanning.createdAndReceivedToast', { name: item.productName || newProductName }) });
        setNotFoundBarcode(null);
        // Raf yerleştirme ekranına yönlendir.
        onPutaway(item);
      } else {
        notify({ type: 'error', message: result.message || t('screens.receiving.scanning.productAcceptFailed') });
      }
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      notify({ type: 'error', message: backendMsg || t('screens.receiving.scanning.errorCreateProduct') });
    } finally {
      setCreatingProduct(false);
    }
  }, [notFoundBarcode, userDetails, newProductName, newProductPrice, newProductVat, session.id, pendingQty, items, onItemsChange, onPutaway, notify, t]);

  const handleQtyUpdate = useCallback(async (itemId: string, newQty: number) => {
    if (newQty < 1) return;
    try {
      await receivingService.updateItemQty(itemId, newQty);
      onItemsChange(items.map(i => i.id === itemId ? { ...i, receivedQuantity: newQty } : i));
      if (lastScanned?.id === itemId) {
        setLastScanned(prev => prev ? { ...prev, quantity: newQty } : null);
        setPendingQty(newQty);
      }
    } catch {
      notify({ type: 'error', message: t('screens.receiving.scanning.errorQtyUpdate') });
    }
  }, [items, onItemsChange, lastScanned, notify, t]);

  const handleRemoveItem = useCallback(async (itemId: string) => {
    try {
      await receivingService.removeItem(itemId);
      onItemsChange(items.filter(i => i.id !== itemId));
      if (lastScanned?.id === itemId) setLastScanned(null);
      notify({ type: 'info', message: t('screens.receiving.scanning.itemRemoved') });
    } catch {
      notify({ type: 'error', message: t('screens.receiving.scanning.errorRemove') });
    }
  }, [items, onItemsChange, lastScanned, notify, t]);

  const handleAcceptQty = useCallback(async () => {
    if (!lastScanned) return;
    if (pendingQty !== lastScanned.quantity) {
      await handleQtyUpdate(lastScanned.id, pendingQty);
    }
    notify({ type: 'success', message: t('screens.receiving.scanning.acceptedToast', { name: lastScanned.productName, count: pendingQty }) });
    setLastScanned(null);
  }, [lastScanned, pendingQty, handleQtyUpdate, notify, t]);

  const totalItems = items.reduce((sum, i) => sum + i.receivedQuantity, 0);
  const unplacedItems = items.filter(i => i.status !== 'PutAway');

  const handleCompleteClick = useCallback(async () => {
    try {
      await onComplete();
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      const names = unplacedItems.map(i => i.productName || i.barcode || t('screens.receiving.scanning.unknownItem'));
      const fallback = names.length > 0
        ? t('screens.receiving.scanning.unplacedItems', { names: names.join(', ') })
        : t('screens.receiving.scanning.completeFailed');
      notify({ type: 'error', message: backendMsg || fallback });
    }
  }, [onComplete, unplacedItems, notify, t]);

  const handleCancelConfirm = useCallback(async () => {
    setCancelling(true);
    try {
      await onCancel(cancelReason.trim() || undefined);
      // Başarılı iptalde üst bileşen ekranı değiştirir; burada dialogu kapatmak yeterli.
      setCancelDialogOpen(false);
      setCancelReason('');
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      notify({ type: 'error', message: backendMsg || t('screens.receiving.scanning.cancelFailed') });
    } finally {
      setCancelling(false);
    }
  }, [onCancel, cancelReason, notify, t]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('screens.receiving.scanning.detailTitle', { session: session.sessionNumber })}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {session.cashierName && (
              <Chip label={t('screens.receiving.scanning.operatorChip', { name: session.cashierName })} size="small" variant="outlined" />
            )}
            <Chip label={t('screens.receiving.scanning.productVarietyChip', { count: items.length })} size="small" color="info" variant="outlined" />
            <Chip label={t('screens.receiving.scanning.totalQtyChip', { count: totalItems })} size="small" color="primary" variant="outlined" />
            <Chip
              label={t('screens.receiving.scanning.placedChip', { placed: items.filter(i => i.status === 'PutAway').length, total: items.length })}
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>

          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {t('screens.receiving.scanning.noItemsYet')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('common.product')}</TableCell>
                  <TableCell>{t('common.barcode')}</TableCell>
                  <TableCell align="center">{t('common.quantity')}</TableCell>
                  <TableCell>{t('common.location')}</TableCell>
                  <TableCell align="center">{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{item.productName || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.barcode || '-'}</TableCell>
                    <TableCell align="center">{item.receivedQuantity}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.locationCode || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.status === 'PutAway' ? t('screens.receiving.scanning.statusPutAway') : t('screens.receiving.scanning.statusReceived')}
                        size="small"
                        color={item.status === 'PutAway' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!variantChoice} onClose={() => setVariantChoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('screens.receiving.scanning.variantTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            {t('screens.receiving.scanning.variantHint', { barcode: variantChoice?.barcode })}
          </Typography>
          <List disablePadding>
            {variantChoice?.variants.map(v => (
              <ListItemButton key={v.productId} onClick={() => handleVariantSelect(v)} sx={{ borderRadius: 1, mb: 0.5, border: '1px solid', borderColor: 'divider' }}>
                <ListItemText
                  primary={v.productName || t('screens.receiving.scanning.variantUnnamed')}
                  secondary={v.stockCode ? t('screens.receiving.scanning.variantStockCode', { code: v.stockCode }) : undefined}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariantChoice(null)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!notFoundBarcode} onClose={() => !creatingProduct && setNotFoundBarcode(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <InventoryIcon color="primary" /> {t('screens.receiving.scanning.notFoundTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{notFoundBarcode}</strong> {t('screens.receiving.scanning.notFoundMessage')}
          </Typography>
          <TextField
            label={t('screens.receiving.scanning.productNameLabel')}
            value={newProductName}
            onChange={e => setNewProductName(e.target.value)}
            fullWidth
            required
            autoFocus
            disabled={creatingProduct}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('screens.receiving.scanning.salePriceLabel')}
              type="number"
              value={newProductPrice}
              onChange={e => setNewProductPrice(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={creatingProduct}
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('screens.receiving.scanning.vatLabel')}
              type="number"
              value={newProductVat}
              onChange={e => setNewProductVat(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={creatingProduct}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              sx={{ width: 110 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotFoundBarcode(null)} disabled={creatingProduct}>
            {t('screens.receiving.scanning.no')}
          </Button>
          <Button
            onClick={handleCreateAndReceive}
            variant="contained"
            color="primary"
            disabled={creatingProduct || !newProductName.trim()}
            startIcon={<InventoryIcon />}
          >
            {creatingProduct ? t('screens.receiving.scanning.adding') : t('screens.receiving.scanning.addAndPlace')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>{t('screens.receiving.scanning.cancelReceivingTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{session.sessionNumber}</strong> {t('screens.receiving.scanning.cancelMessage')}
          </Typography>
          <TextField
            label={t('screens.receiving.scanning.cancelReasonLabel')}
            placeholder={t('screens.receiving.scanning.cancelReasonPlaceholder')}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={cancelling}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            {t('common.back')}
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={<CancelIcon />}
          >
            {cancelling ? t('screens.receiving.scanning.cancelling') : t('screens.receiving.scanning.cancelConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <WmsStationHeader
        title={t('screens.receiving.scanning.stationTitle', { session: session.sessionNumber })}
        subtitle={t('screens.receiving.scanning.stationSubtitle')}
        badgeLabel={t('screens.receiving.scanning.acceptBadge', { count: totalItems })}
        onBadgeClick={() => setDetailOpen(true)}
      />

      <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>{t('screens.receiving.scanning.scanProductTitle')}</Typography>
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <WmsBarcodeScanner onScan={handleScan} placeholder={t('screens.receiving.scanning.scanProductPlaceholder')} />
        </Box>
      </Paper>

      {lastScanned && (
        <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>{t('screens.receiving.scanning.lastScanned')}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{lastScanned.productName}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {t('screens.receiving.scanning.barcodeLabel', { barcode: lastScanned.barcode })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">{t('screens.receiving.scanning.quantityLabel')}</Typography>
            <IconButton size="small" onClick={() => setPendingQty(Math.max(1, pendingQty - 1))}>
              <RemoveIcon />
            </IconButton>
            <TextField
              value={pendingQty}
              onChange={e => {
                const v = parseInt(e.target.value);
                if (!isNaN(v) && v > 0) setPendingQty(v);
              }}
              size="small"
              type="number"
              sx={{ width: 80 }}
              InputProps={{
                inputProps: { min: 1, style: { textAlign: 'center' } },
              }}
            />
            <IconButton size="small" onClick={() => setPendingQty(pendingQty + 1)}>
              <AddIcon />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleAcceptQty}
              color="success"
              sx={{ fontWeight: 600 }}
            >
              {t('screens.receiving.scanning.accept')}
            </Button>
          </Box>

          {scanSuggestion?.suggestedLocationId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('screens.receiving.scanning.suggestedOnMap')}
              </Typography>
              <ReceivingNavMap
                warehouseId={warehouseId}
                targetLocationId={scanSuggestion.suggestedLocationId}
                targetLocationCode={scanSuggestion.locationCode}
                subtitle={scanSuggestion.reason}
                height={240}
              />
            </Box>
          )}
        </Paper>
      )}

      <WmsListSection
        title={t('screens.receiving.scanning.receivedListTitle')}
        count={items.length}
        hasData={items.length > 0}
        emptyText={t('screens.receiving.scanning.noItemsYet')}
      >
        <Paper sx={{ bgcolor: 'background.paper', overflow: 'auto' }}>
          <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('common.product')}</TableCell>
                  <TableCell align="center">{t('screens.receiving.scanning.colQty')}</TableCell>
                  <TableCell>{t('common.location')}</TableCell>
                  <TableCell align="center">{t('common.status')}</TableCell>
                  <TableCell align="center">{t('common.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.id} sx={{ opacity: item.status === 'PutAway' ? 0.6 : 1 }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{item.productName || '-'}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleQtyUpdate(item.id, item.receivedQuantity - 1)}
                          disabled={item.receivedQuantity <= 1 || item.status === 'PutAway'}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{item.receivedQuantity}</Typography>
                        <IconButton size="small" onClick={() => handleQtyUpdate(item.id, item.receivedQuantity + 1)}
                          disabled={item.status === 'PutAway'}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {item.locationCode || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.status === 'PutAway' ? t('screens.receiving.scanning.statusPutAway') : t('screens.receiving.scanning.statusReceived')}
                        size="small"
                        color={item.status === 'PutAway' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {item.status !== 'PutAway' && (
                          <IconButton size="small" color="primary" onClick={() => onPutaway(item)}>
                            <PlaceIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)}
                          disabled={item.status === 'PutAway'}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </Table>
        </Paper>
      </WmsListSection>

      <WmsPrimaryActionBar
        secondaryAction={(
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setCancelDialogOpen(true)}
              size="large"
              startIcon={<CancelIcon />}
            >
              {t('screens.receiving.scanning.cancelReceivingButton')}
            </Button>
            {unplacedItems.length > 0 && (
              <Button variant="outlined" onClick={onBulkPutaway} size="large" startIcon={<PlaceIcon />}>
                {t('screens.receiving.scanning.bulkPutaway', { count: unplacedItems.length })}
              </Button>
            )}
          </Box>
        )}
        primaryAction={(
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleCompleteClick}
            disabled={items.length === 0}
            sx={{ minWidth: 220, fontSize: 16, fontWeight: 700 }}
          >
            {t('screens.receiving.scanning.completeDelivery')}
          </Button>
        )}
      />
    </Box>
  );
};

export default ReceivingScanning;
